"use client";

import {
  Info,
  Users,
  BookOpen,
  Award,
  MessageSquare,
  Calendar,
} from "lucide-react";
import Link from "next/link";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";


interface AdPlaceholderProps {
  type: "sidebar" | "banner" | "sponsored-post";
  className?: string;
  isPromotional?: boolean;
  promotionalType?:
    | "study-groups"
    | "tutoring"
    | "certification-tracking"
    | "forums"
    | "study-planner";
  onClick?: () => void;
}

const PLACEHOLDER_IMAGES = {
  study: [
    "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1513258496099-48168024aec0?w=800&auto=format&fit=crop",
  ],
  certification: [
    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop",
  ],
  success: [
    "https://images.unsplash.com/photo-1552581234-26160f608093?w=800&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop",
  ],
};

const PROMOTIONAL_CONTENT = [
  {
    title: "Join Study Groups",
    description: "Connect with peers preparing for the same certification",
    icon: Users,
    link: "/study-groups",
    image: "https://images.unsplash.com/photo-1513258496099-48168024aec0?w=800&auto=format&fit=crop",
  },
  {
    title: "Expert Tutoring",
    description: "Get personalized guidance from certified professionals",
    icon: BookOpen,
    link: "/tutors",
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&auto=format&fit=crop",
  },
  {
    title: "Certification Tracking",
    description: "Manage and track your certification progress",
    icon: Award,
    link: "/certifications",
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop",
  },
  {
    title: "Community Forums",
    description: "Join discussions with certification candidates",
    icon: MessageSquare,
    link: "/forums",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&auto=format&fit=crop",
  },
  {
    title: "Study Planner",
    description: "Plan your certification journey with our tools",
    icon: Calendar,
    link: "/free-study-planner",
    image:
      "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&auto=format&fit=crop",
  },
];

export function AdPlaceholder({
  type = "sidebar",
  className = "",
  isPromotional = false,
  promotionalType,
  onClick,
}: AdPlaceholderProps) {
  // If promotional, select content based on type or random
  if (isPromotional) {
    const content = promotionalType
      ? PROMOTIONAL_CONTENT.find((c) => c.link.includes(promotionalType)) ||
        PROMOTIONAL_CONTENT[0]
      : PROMOTIONAL_CONTENT[
          Math.floor(Math.random() * PROMOTIONAL_CONTENT.length)
        ];
    const Icon = content.icon;

    if (type === "sidebar") {
      return (
        <Card className={`overflow-hidden ${className}`} onClick={onClick}>
          <div className="relative">
            <img
              src={content.image}
              alt={content.title}
              className="w-full h-48 object-cover"
            />
            <Badge
              variant="secondary"
              className="absolute top-2 right-2 text-xs"
            >
              Featured
            </Badge>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon className="h-5 w-5 text-primary" />
              <h3 className="font-medium">{content.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {content.description}
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link href={content.link}>Learn More</Link>
            </Button>
          </div>
        </Card>
      );
    }

    if (type === "banner") {
      return (
        <Card className={`flex flex-col md:flex-row items-stretch overflow-hidden shadow-lg border-2 border-primary-100 bg-white ${className}`} style={{ borderRadius: 16 }} onClick={onClick}>
          {/* Left: Icon, Title, Description, Button */}
          <div className="flex flex-col justify-center p-6 md:w-2/3 w-full bg-primary-50" style={{ borderTopLeftRadius: 16, borderBottomLeftRadius: 16 }}>
            <div className="flex items-center gap-3 mb-2">
              <span className="inline-flex items-center justify-center rounded-full bg-primary-600 text-white w-10 h-10">
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="font-bold text-lg text-primary-700">{content.title}</h3>
            </div>
            <p className="text-base text-muted-foreground mb-4">{content.description}</p>
            <Button asChild className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2 rounded-full w-max shadow-md">
              <Link href={content.link}>Learn More</Link>
            </Button>
          </div>
          {/* Right: Image */}
          <div className="md:w-1/3 w-full flex items-center justify-center bg-white" style={{ borderTopRightRadius: 16, borderBottomRightRadius: 16 }}>
            <img
              src={content.image}
              alt={content.title}
              className="object-cover w-full h-32 md:h-full md:w-full rounded-none md:rounded-r-2xl"
              style={{ minHeight: 80, maxHeight: 128 }}
            />
          </div>
        </Card>
      );
    }

    // Sponsored post style
    return (
      <Card className={`overflow-hidden ${className}`} onClick={onClick}>
        <div className="flex items-start gap-4 p-4">
          <img
            src={content.image}
            alt={content.title}
            className="w-24 h-24 object-cover rounded-md"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Icon className="h-5 w-5 text-primary" />
              <Badge variant="secondary" className="text-xs">
                Featured
              </Badge>
            </div>
            <h3 className="font-medium mb-1">{content.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {content.description}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link href={content.link}>Learn More</Link>
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Original ad placeholder code for non-promotional ads
  const categories = Object.keys(PLACEHOLDER_IMAGES) as Array<
    keyof typeof PLACEHOLDER_IMAGES
  >;
  const randomCategory =
    categories[Math.floor(Math.random() * categories.length)];
  const images = PLACEHOLDER_IMAGES[randomCategory];
  const randomImage = images[Math.floor(Math.random() * images.length)];

  if (type === "sidebar") {
    return (
      <Card className={`overflow-hidden ${className}`} onClick={onClick}>
        <div className="relative">
          <img
            src={randomImage}
            alt="Advertisement placeholder"
            className="w-full h-48 object-cover"
          />
          <Badge variant="secondary" className="absolute top-2 right-2 text-xs">
            Ad Space Available
          </Badge>
        </div>
        <div className="p-4">
          <h3 className="font-medium mb-2">Advertise Your Services Here</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Reach certification candidates looking for study resources and
            tutoring.
          </p>
          <Button asChild variant="outline" size="sm" className="w-full">
            <Link href="/advertise">Learn More</Link>
          </Button>
        </div>
      </Card>
    );
  }

  if (type === "banner") {
    return (
      <Card className={`overflow-hidden ${className}`} onClick={onClick}>
        <div className="relative">
          <img
            src={randomImage}
            alt="Advertisement placeholder"
            className="w-full h-32 object-cover"
          />
          <Badge variant="secondary" className="absolute top-2 right-2 text-xs">
            Premium Ad Space
          </Badge>
        </div>
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            Premium banner advertisement space — promote your certification
            resources to thousands of professionals.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link href="/advertise">Advertise Here</Link>
          </Button>
        </div>
      </Card>
    );
  }

  // Sponsored post style
  return (
    <Card className={`overflow-hidden ${className}`} onClick={onClick}>
      <div className="flex items-start gap-4 p-4">
        <img
          src={randomImage}
          alt="Advertisement placeholder"
          className="w-24 h-24 object-cover rounded-md"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="text-xs">
              Sponsored
            </Badge>
            <span className="text-sm text-muted-foreground">
              Ad Space Available
            </span>
          </div>
          <h3 className="font-medium mb-1">
            Promote Your Certification Program
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            Connect with certification candidates through sponsored content
            placement.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link href="/advertise">Get Started</Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}
